import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './CISCalculator.css';

const CISCalculator = () => {
  // State for form inputs with default example values - KEEPING AS IS ✓
  const [labour, setLabour] = useState('500');
  const [materials, setMaterials] = useState('83');
  const [cisRate, setCisRate] = useState('20');
  const [vatRegistered, setVatRegistered] = useState(true);
  
  // State for calculated results
  const [results, setResults] = useState(null);
  const [openFaq, setOpenFaq] = useState(null); // For accordion
  
  // State for validation errors
  const [errors, setErrors] = useState({});

  // Format currency in GBP
  const formatGBP = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Validate inputs
  const validateInputs = () => {
    const newErrors = {};
    
    if (!labour || labour === '') {
      newErrors.labour = 'Labour amount is required';
    } else if (isNaN(labour) || labour < 0) {
      newErrors.labour = 'Please enter a valid positive number';
    }
    
    if (!materials || materials === '') {
      newErrors.materials = 'Materials amount is required';
    } else if (isNaN(materials) || materials < 0) {
      newErrors.materials = 'Please enter a valid positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calculate results on button click
  const handleCalculate = () => {
    if (!validateInputs()) return;

    const labourNum = parseFloat(labour) || 0;
    const materialsNum = parseFloat(materials) || 0;
    const cisRateNum = parseFloat(cisRate) || 0;
    const vatRateNum = 20; // Fixed at 20% for UK

    // Calculate CIS deduction (only on labour)
    const cisDeductionValue = (labourNum * cisRateNum) / 100;
    
    // Calculate net labour
    const netLabourValue = labourNum - cisDeductionValue;
    
    // Calculate pay before VAT (net labour + materials)
    const payBeforeVatValue = netLabourValue + materialsNum;
    
    // Calculate VAT if registered (fixed 20%)
    let vatAmountValue = 0;
    if (vatRegistered) {
      // VAT is charged on full invoice amount (labour + materials)
      const totalBeforeVat = labourNum + materialsNum;
      vatAmountValue = (totalBeforeVat * vatRateNum) / 100;
    }
    
    // Calculate total payable
    const totalPayableValue = payBeforeVatValue + vatAmountValue;

    // Set results
    setResults({
      cisDeduction: cisDeductionValue,
      netLabour: netLabourValue,
      payBeforeVat: payBeforeVatValue,
      vatAmount: vatAmountValue,
      totalPayable: totalPayableValue
    });
  };

  // Handle input changes
  const handleLabourChange = (e) => {
    setLabour(e.target.value);
    setResults(null);
  };

  const handleMaterialsChange = (e) => {
    setMaterials(e.target.value);
    setResults(null);
  };

  const handleCisRateChange = (e) => {
    setCisRate(e.target.value);
    setResults(null);
  };

  const handleVatRegisteredChange = (e) => {
    setVatRegistered(e.target.checked);
    setResults(null);
  };

  // Set example values - KEEPING AS IS ✓
  const setExample1 = () => {
    setLabour('500');
    setMaterials('83');
    setCisRate('20');
    setVatRegistered(true);
    setResults(null);
  };

  const setExample2 = () => {
    setLabour('1000');
    setMaterials('200');
    setCisRate('20');
    setVatRegistered(true);
    setResults(null);
  };

  const setExample3 = () => {
    setLabour('750');
    setMaterials('150');
    setCisRate('30');
    setVatRegistered(false);
    setResults(null);
  };

  // Reset all fields
  const handleReset = () => {
    setLabour('');
    setMaterials('');
    setCisRate('20');
    setVatRegistered(false);
    setResults(null);
    setErrors({});
  };

  // Toggle FAQ accordion
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // FAQ data - EXACT TEXT AS REQUESTED
  const faqs = [
    {
      question: "What is CIS and who does it apply to?",
      answer: "The Construction Industry Scheme (CIS) requires contractors to deduct tax from payments made to subcontractors and pass it to HMRC. It applies to most construction work carried out in the UK, including building, repairs, demolition and civil engineering work."
    },
    {
      question: "Is CIS deducted from the full invoice amount?",
      answer: "No. CIS is deducted from the labour portion only. Materials, VAT and certain equipment costs are not subject to CIS deduction. This calculator applies the deduction to labour only."
    },
    {
      question: "What are the CIS deduction rates?",
      answer: "There are three main deduction rates: 0% for subcontractors with Gross Payment Status, 20% for HMRC-verified subcontractors, 30% for unverified subcontractors. The correct rate must be confirmed through HMRC verification."
    },
    {
      question: "Is VAT included when calculating CIS?",
      answer: "CIS is calculated before VAT is added. VAT is charged on the full supply, but the CIS deduction applies only to the labour element."
    },
    {
      question: "Do I need to verify a subcontractor before paying them?",
      answer: "Yes. Contractors must verify new subcontractors with HMRC before making payment to confirm the correct deduction rate."
    },
    {
      question: "Is this calculator HMRC compliant?",
      answer: "This calculator follows current HMRC CIS deduction rules and is provided as a guidance tool. Contractors remain responsible for proper verification and submitting accurate monthly CIS returns."
    }
  ];

  return (
    <>
      <Helmet>
        <title>CIS Invoice Payment Deduction Calculator | Free UK Construction Industry Scheme Tool | PayCIS</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes"></meta>
        <meta name="description" content="Free CIS deduction calculator for UK contractors. Instantly calculate 0%, 20% or 30% CIS on labour, with VAT separated correctly and total payable shown clearly." />
        <meta name="keywords" content="CIS calculator, Construction Industry Scheme, CIS deduction, subcontractor calculator, UK tax, HMRC CIS, labour only deduction, VAT calculator, CIS rates 2024" />
        <meta name="author" content="PayCIS" />
        <meta property="og:title" content="CIS Invoice Payment Deduction Calculator | PayCIS" />
        <meta property="og:description" content="Free CIS deduction calculator for UK contractors. Instantly calculate 0%, 20% or 30% CIS on labour, with VAT separated correctly." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paycis.co.uk/cis-calculator" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="CIS Invoice Payment Deduction Calculator" />
        <meta name="twitter:description" content="Free CIS deduction calculator for UK contractors. Instantly calculate CIS on labour with VAT separated." />
        <link rel="canonical" href="https://paycis.co.uk/cis-calculator" />
      </Helmet>

      <div className="cis-calculator">
        {/* NEW H1 - Exact as requested */}
        <h1>CIS Deduction Calculator</h1>
        
        {/* NEW Short Intro - Exact text, below H1 */}
        <div className="calculator-intro" style={{
          background: 'rgba(91, 192, 222, 0.1)',
          padding: '1.2rem 1.5rem',
          marginBottom: '2rem',
          borderRadius: '0 12px 12px 0',
          color: 'white',
          fontSize: '1.05rem',
          lineHeight: '1.6',
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <p style={{ margin: 0 }}>
            <strong>Use this free CIS deduction calculator</strong> to quickly calculate how much tax to deduct from a payment made under the Construction Industry Scheme (CIS).<br />
            Enter labour and materials separately, apply the correct 0%, 20% or 30% deduction rate, and see the total payable amount instantly.
          </p>
        </div>

        {/* Original subtitle - KEEPING AS IS */}
        <p className="subtitle">
          Calculate your take-home pay after CIS deductions
        </p>

        <div className="calculator-container">
          <div className="calculator-vertical">
            {/* Input Section - EXACTLY AS YOUR ORIGINAL */}
            <div className="inputs-section">
              <h3>Enter Invoice Details</h3>
              
              <div className="calculator-form">
                {/* Labour Input */}
                <div className="input-group">
                  <label>Labour Amount</label>
                  <input
                    type="number"
                    value={labour}
                    onChange={handleLabourChange}
                    placeholder="e.g., 500"
                    min="0"
                    step="0.01"
                    className={errors.labour ? 'error' : ''}
                  />
                  {errors.labour && <span className="error-message">{errors.labour}</span>}
                </div>

                {/* Materials Input */}
                <div className="input-group">
                  <label>Materials Amount</label>
                  <input
                    type="number"
                    value={materials}
                    onChange={handleMaterialsChange}
                    placeholder="e.g., 83"
                    min="0"
                    step="0.01"
                    className={errors.materials ? 'error' : ''}
                  />
                  {errors.materials && <span className="error-message">{errors.materials}</span>}
                </div>

                {/* Example Buttons - KEEPING AS YOUR ORIGINAL */}
                <div className="example-buttons">
                  <button className="example-btn" onClick={setExample1}>
                    Example 1: £500 + £83
                  </button>
                  <button className="example-btn" onClick={setExample2}>
                    Example 2: £1000 + £200
                  </button>
                  <button className="example-btn" onClick={setExample3}>
                    Example 3: £750 + £150 (No VAT)
                  </button>
                </div>

                {/* CIS Rate - KEEPING AS YOUR ORIGINAL */}
                <div className="radio-section">
                  <label>CIS Tax Rate</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="0"
                        checked={cisRate === '0'}
                        onChange={handleCisRateChange}
                      />
                      0% (Gross)
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="20"
                        checked={cisRate === '20'}
                        onChange={handleCisRateChange}
                      />
                      20% (Standard)
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="30"
                        checked={cisRate === '30'}
                        onChange={handleCisRateChange}
                      />
                      30% (Higher)
                    </label>
                  </div>
                </div>

                {/* VAT Checkbox - KEEPING AS YOUR ORIGINAL */}
                <div className="vat-section">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="vatRegistered"
                      checked={vatRegistered}
                      onChange={handleVatRegisteredChange}
                    />
                    <label htmlFor="vatRegistered">VAT Registered (20%)</label>
                  </div>
                  <div className="vat-note">
                    UK VAT is fixed at 20% - automatically applied when checked
                  </div>
                </div>

                {/* Action Buttons - KEEPING AS YOUR ORIGINAL */}
                <div className="action-buttons">
                  <button className="calculate-btn" onClick={handleCalculate}>
                    GENERATE CALCULATION
                  </button>
                  <button className="reset-btn" onClick={handleReset}>
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Results Section - KEEPING AS YOUR ORIGINAL */}
            <div className="results-section">
              <h3>Calculation Results</h3>
              
              {results ? (
                <div className="results-grid">
                  <div className="result-item">
                    <span className="label">CIS Deduction:</span>
                    <span className="value">{formatGBP(results.cisDeduction)}</span>
                  </div>
                  
                  <div className="result-item">
                    <span className="label">Net Labour:</span>
                    <span className="value">{formatGBP(results.netLabour)}</span>
                  </div>
                  
                  <div className="result-item">
                    <span className="label">Pay Before VAT:</span>
                    <span className="value">{formatGBP(results.payBeforeVat)}</span>
                  </div>
                  
                  {vatRegistered && (
                    <div className="result-item">
                      <span className="label">VAT Amount (20%):</span>
                      <span className="value">{formatGBP(results.vatAmount)}</span>
                    </div>
                  )}
                  
                  <div className="result-item total">
                    <span className="label">Total Payable:</span>
                    <span className="value">{formatGBP(results.totalPayable)}</span>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>👆</p>
                  <p>Enter details and click GENERATE CALCULATION to see results</p>
                </div>
              )}

              {/* Example Note - KEEPING AS YOUR ORIGINAL */}
              <div className="example-note">
                <p>
                  <strong>📌 How it works:</strong> CIS is deducted on labour only. 
                  VAT (20%) is charged on full invoice amount if registered.
                </p>
                <p style={{ marginTop: '0.5rem', fontSize: '0.95rem' }}>
                  <strong>Example:</strong> Labour £500 + Materials £83 @20% CIS<br />
                  = £100 deduction → Net labour £400 + Materials £83 = £483 before VAT<br />
                  With VAT (20%): £483 + £116.60 = <strong>£599.60 total</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* NEW FAQ Section - Accordion Style */}
        <div className="faq-section" style={{
          marginTop: '4rem',
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <h2 style={{
            color: 'white',
            fontSize: '2rem',
            marginBottom: '2rem',
            textAlign: 'center',
          }}>Frequently Asked Questions</h2>
          
          <div className="faq-container" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                overflow: 'hidden'
              }}>
                <button
                  onClick={() => toggleFaq(index)}
                  style={{
                    width: '100%',
                    padding: '1.2rem',
                    background: 'rgba(91, 192, 222, 0.1)',
                    border: 'none',
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(91, 192, 222, 0.2)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(91, 192, 222, 0.1)'}
                >
                  {faq.question}
                  <span style={{
                    fontSize: '1.5rem',
                    color: '#57bedd',
                    transform: openFaq === index ? 'rotate(45deg)' : 'none',
                    transition: 'transform 0.2s'
                  }}>
                    {openFaq === index ? '−' : '+'}
                  </span>
                </button>
                
                {openFaq === index && (
                  <div style={{
                    padding: '1.2rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: '1.6',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* NEW Conversion Line - Below FAQs */}
        <div className="conversion-section" style={{
          marginTop: '3rem',
          padding: '2rem',
          background: 'linear-gradient(135deg, rgba(91,192,222,0.2), rgba(91,192,222,0.05))',
          borderRadius: '16px',
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto',
          textAlign: 'center',
          border: '1px solid rgba(91,192,222,0.3)'
        }}>
          <p style={{
            color: 'white',
            fontSize: '1.2rem',
            marginBottom: '0',
            lineHeight: '1.6'
          }}>
            <strong>Want to manage CIS deductions, subcontractor verification and monthly returns automatically?</strong><br />
            See how <a href="/" style={{
              color: '#57bedd',
              textDecoration: 'none',
              fontWeight: '600',
              borderBottom: '2px solid #57bedd',
              paddingBottom: '2px'
            }}>PayCIS simplifies CIS compliance</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default CISCalculator;
